import React, { Fragment, useState, useCallback } from 'react';
import { Button, Col, Row, Stack, Dropdown, Alert } from 'react-bootstrap';
import ContentLoader from '../../Components/ContentLoader';
import SimpleActionTable from '../../Components/SimpleActionTable';
import {
  LIST_ATTRIBUTES_WITH_FILTER_OPERATIONS,
  LIST_INDICATORS_WITH_MANDATORY_ATTRIBUTE,
} from '../../graphql/queries';
import {
  FilterValueOption,
  IndicatorFilterOperationOption,
  type IndicatorFilterType,
  type IndicatorSearchInput,
  type ListIndicatorsWithMandatoryAttributesQuery,
  type ListAttributesWithFilterOperationsQuery,
  AttributeValueOption,
} from '../../__generated__/graphql';
import {
  type StandardIndicatorFilterOperationOptionMapping,
  type SimpleIndicator,
  type FilterType,
  type MetadataForFilter,
} from '../../graphql/types';
import { ButtonLoading } from '../../Components/ButtonLoading';
import { FileEarmarkFill, PencilFill, Trash3Fill } from 'react-bootstrap-icons';
import SimpleTooltip from '../../Components/SimpleTooltip';
import { DELETE_INDICATOR } from '../../graphql/mutations';
import { useMutation, useQuery } from '@apollo/client';
import AggregateErrors from '../../utils/AggregateErrors';
import SimpleActionModal from '../../Components/SimpleActionModal';
import FloatingTextInput from '../../Components/FloatingTextInput';
import FloatingSelectInput from '../../Components/FloatingSelectInput';
import './indicators.scss';
import FloatingDateInput from '../../Components/FloatingDateInput';
import { operationToGraphqlMapping } from '../../utils/MappingOperations';


/**
 * la variable `metadataFilters` de `Filtertype` pour les métadonnées est défini ici puisque les métadonnées ne sont pas
 * définies dans le schéma GraphQL. Le type est défini localement pour permettre l'affichage
 * des filtres sur les métadonnées. L'application web s'occupe de convertir le filtre 
 * pour métadonnées spécifié ici pour l'API.
 * 
 * Pour le champ Auteur, le filtre est appliqué sur le prénom et le nom de famille.
 * Le filtre est appliqué sur les deux champs avec un opérateur `OR`.
 */
const metadataFilters: MetadataForFilter[] = [
  {
    id: 'createdOn',
    name: 'Créé le',
    valueType: FilterValueOption.Date,
    acceptedIndicatorFilterOperations: [
      {
        enumValue: IndicatorFilterOperationOption.Equals,
        readableValue: 'Est le',
        negatedReadableValue: "N'est pas le",
      },
      {
        enumValue: IndicatorFilterOperationOption.GreaterThan,
        readableValue: 'Est après',
        negatedReadableValue: "N'est pas après",
      },
      {
        enumValue: IndicatorFilterOperationOption.LesserThan,
        readableValue: 'Est avant',
        negatedReadableValue: "N'est pas avant",
      },
      {
        enumValue: IndicatorFilterOperationOption.IsDefined,
        readableValue: 'Est défini',
        negatedReadableValue: "N'est pas défini",
      },
    ],
  },
    {
    id: 'editedOn',
    name: 'Modifié le',
    valueType: FilterValueOption.Date,
    acceptedIndicatorFilterOperations: [
      {
        enumValue: IndicatorFilterOperationOption.Equals,
        readableValue: 'Est le',
        negatedReadableValue: "N'est pas le",
      },
      {
        enumValue: IndicatorFilterOperationOption.GreaterThan,
        readableValue: 'Est après',
        negatedReadableValue: "N'est pas après",
      },
      {
        enumValue: IndicatorFilterOperationOption.LesserThan,
        readableValue: 'Est avant',
        negatedReadableValue: "N'est pas avant",
      },
      {
        enumValue: IndicatorFilterOperationOption.IsDefined,
        readableValue: 'Est défini',
        negatedReadableValue: "N'est pas défini",
      },
    ],
  },
    {
    id: 'owner',
    name: 'Auteur',
    valueType: FilterValueOption.Characters,
    acceptedIndicatorFilterOperations: [
      {
        enumValue: IndicatorFilterOperationOption.Equals,
        readableValue: 'Est le',
        negatedReadableValue: "N'est pas le",
      },
      {
        enumValue: IndicatorFilterOperationOption.StartsWith,
        readableValue: 'Commence par',
        negatedReadableValue: "Ne commence pas par",
      },
      {
        enumValue: IndicatorFilterOperationOption.EndsWith,
        readableValue: 'Finit par',
        negatedReadableValue: "Ne finit pas par",
      },
      {
        enumValue: IndicatorFilterOperationOption.IsDefined,
        readableValue: 'Est défini',
        negatedReadableValue: "N'est pas défini",
      },
      {
        enumValue: IndicatorFilterOperationOption.Contains,
        readableValue: 'Contient',
        negatedReadableValue: "Ne contient pas",
      },
    ],
  }
];


/**
 * Type pour un attribut d'indicateur. 
 * Les deux `NonNullable` sont nécessaires, puisque l'API GraphQL peut retourner `null` pour les attributs
 * et les items d'attributs. Le type `Attribute` est utilisé pour les filtres sur les attributs d'indicateur.
 */
export type Attribute = NonNullable<NonNullable<ListAttributesWithFilterOperationsQuery['attributes']>['items']>[number];

/**
 * Propriétés communes à tous les filtres d'indicateurs.
 */
interface IndicatorFilterBase {
  operation: StandardIndicatorFilterOperationOptionMapping;
  negate: boolean;
  value: string;
}

/**
 * Filtre basé sur une métadonnée de l'indicateur.
 */
export interface IndicatorMetadataFilter extends IndicatorFilterBase {
  kind: 'metadata';
  filter: MetadataForFilter;
}

/**
 * Filtre basé sur un attribut d'indicateur.
 */
export interface IndicatorAttributeFilter extends IndicatorFilterBase {
  kind: 'attribute';
  filter: FilterType;
}

/**
 * Filtre d'indicateur, applicable soit sur une métadonnée, soit sur un attribut d'indicateur.
 */
export type IndicatorFilter = IndicatorMetadataFilter | IndicatorAttributeFilter;


/**
 * Ce composant permet d'afficher la liste des indicateurs présent dans l'application
 * en fonction des attributs d'indicateur obligatoires
 * @returns Le composant rendu par React
 */
export function Indicators(): React.ReactElement {
  // <- Variables d'états ->
  /**
   * Le nombre d'élément contenu dans la page
   */
  const [skippedIndicators, setSkippedIndicators] = useState(0);
  const [pageSize, setPageSize] = useState(
    parseInt(localStorage.getItem('indicatorPageSize') ?? '20')
  );
  const [confirmModalShow, setConfirmModalShow] = useState(false);
  // État pour la suppression d'un indicateur.
  const [indicatorToDelete, setIndicatorToDelete] = useState<SimpleIndicator>();
  const [successDeleteMessage, setSuccessDeleteMessage] = useState('');
  const [filterActive, setFilterActive] = useState(false);
  const [filterShow, setFilterShow] = useState(false);
  const [attributeName, setAttributeName] = useState('');
  const [currentFilter, setCurrentFilter] = useState('');
  const [filters, setFilters] = useState<IndicatorFilter[]>([]);
  const [hasFilterBeenModified, setFilterBeenModified] = useState(false);
  const [whereStatement, setWhereStatement] = useState<IndicatorFilterType | undefined>();
  const [search, setSearch] = useState<IndicatorSearchInput | undefined>(
    undefined
  );
  // <- GraphQL ->
  const [deleteIndicator, { loading, data, error }] = useMutation(
    DELETE_INDICATOR,
    { refetchQueries: [LIST_INDICATORS_WITH_MANDATORY_ATTRIBUTE] }
  );

  const unusedMetadataFiltersCount = metadataFilters.filter(
    metadataFilter =>
      !filters.some(
        filter => filter.kind === 'metadata' && filter.filter.id === metadataFilter.id
      )
  ).length;

  const take = Math.max(10 - unusedMetadataFiltersCount, 0);

  /**
   * Fonction d'association entre les types d'attributs et les types de filtres.
   * Cette fonction est nécessaire, puisque les types d'attributs et les types de filtres ne sont pas identiques.
   * L'association est définie dans la variable `attributeToFilterValueOptionMapping`.
   * @param value Le type d'attribut à associer.
   * @returns Le type de filtre correspondant.
   */
  const attributeToFilterValueOptionMapping: Record<AttributeValueOption, FilterValueOption> = {
    [AttributeValueOption.Characters]: FilterValueOption.Characters,
    [AttributeValueOption.List]: FilterValueOption.List,
    [AttributeValueOption.Number]: FilterValueOption.Number,
  };

  /**
   * Associe un AttributeValueOption à un FilterValueOption.
   * @param value Le AttributeValueOption à associer.
   * @returns Le FilterValueOption correspondant.
   */
  const mapAttributeValueOptionToFilterValueOption = (
    value: AttributeValueOption
  ): FilterValueOption => {
    return attributeToFilterValueOptionMapping[value];
  }

  /**
   * Fonction d'association entre un attribut et un filtre. Cette fonction est nécessaire, puisque les 
   * types d'attributs et les types de filtres ne sont pas identiques.
   * @param attribute L'attribut à associer.
   * @returns Le filtre correspondant.
   */
  const mapAttributeToFilterType = (
    attribute: Attribute
  ): FilterType => {
      return {
      id: attribute.id,
      name: attribute.name,
      values: attribute.values,
      valueType: mapAttributeValueOptionToFilterValueOption(attribute.valueType),
      acceptedIndicatorFilterOperations: attribute.acceptedIndicatorFilterOperations,
    };
  };

  const {
    loading: attributesLoading,
    data: attributeData,
    error: attributeError,
  } = useQuery(LIST_ATTRIBUTES_WITH_FILTER_OPERATIONS, {
    variables: {
      name: attributeName,
      selected: filters.map(f => f.filter.id),
      take,
    },
  }); 

  /**
   * Fonction qui permet de construire la clause `where` pour la requête GraphQL en fonction des filtres
   * de métadonnées sélectionnées par l'utilisateur.
   * @param metadataFilters Les filtres de métadonnées sélectionnées par l'utilisateur.
   * @returns La clause `where` pour la requête GraphQL ou `undefined` si aucun filtre n'est sélectionné.
   */
  const buildMetadataWhereClause = (
    metadataFilters: IndicatorFilter[]
  ): IndicatorFilterType | undefined => {
    if(metadataFilters.length === 0) {
      return undefined;
    }

    const where: IndicatorFilterType = {};
    metadataFilters.forEach(f => {
      const key = operationToGraphqlMapping(f.operation.enumValue, f.negate);

      switch (f.filter.id) {
        case 'createdOn':
          where.createdOn = { ...where.createdOn, [key]: f.value };
          break;
        case 'editedOn':
          where.editedOn = { ...where.editedOn, [key]: f.value };
          break;
        case 'owner':
          where.owner = { fullName: { [key]: f.value } };
          break;
        default:
          break;
      }
    });
    return where;
  }

  // Variables pour la suppression d'un indicateur
  const deleteErrors = AggregateErrors(error, data?.deleteIndicator.errors);

  // <- Fonctions de rappels ->

  /**
   * Fonction de rappel lorsque le nombre d'éléments par page est changé.
   *
   * @param size Le nouveau nombre d'élément à afficher par page.
   */
  const onPageSizeChange = useCallback((size: number): void => {
    localStorage.setItem('indicatorPageSize', size.toString());
    setPageSize(size);
  }, []);

  /**
   * Fonction de rappel lorsque la page est changé.
   *
   * @param pageNumber Le nouveau numéro de page.
   */
  const onPageSwitch = useCallback(
    (pageNumber: number): void => {
      setSkippedIndicators((pageNumber - 1) * pageSize);
    },
    [pageSize]
  );

  /**
   * Fonction de rappel lorsqu'une personne souhaite supprimer un indicateur.
   *
   * @param {SimpleIndicator} indicator L'indicateur que l'on souhaite supprimer.
   */
  const onDeleteIndicator = (indicator: SimpleIndicator): void => {
    setIndicatorToDelete(indicator);
    setConfirmModalShow(true);
  };

  /**
   * Fonction de rappel lorsqu'une personne annule la suppression d'un indicateur.
   */
  const onClose = useCallback((): void => {
    setConfirmModalShow(false);
    setIndicatorToDelete(undefined);
  }, []);

  /**
   * Fonction de rappel lorsqu'une personne confirme la suppression d'un indicateur.
   */
  const onConfirm: () => void = useCallback(() => {
    const id = indicatorToDelete?.id ?? '';
    if (id === '') return;

    setConfirmModalShow(false);
    setIndicatorToDelete(undefined);

    void (async () => {
      try {
        const result = await deleteIndicator({
          variables: { id },
          onError: () => {
            // Le Hook affichera l'erreur par la suite
          },
        });

        if (
          (result.data?.deleteIndicator.errors ?? []).length === 0 &&
          (result.data?.deleteIndicator.indicator?.id ?? '') !== ''
        ) {
          setSuccessDeleteMessage("L'indicateur a été supprimé avec succès");
          setTimeout(() => { setSuccessDeleteMessage('') }, 3000);
        }
      } catch {
      }
    })();
  }, [deleteIndicator, indicatorToDelete]);

  /**
   * Fonction lorsqu'une personne écrit le nom d'un attribut.
   */
  const onAttributeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setAttributeName(event.target.value);
    },
    []
  );

  /**
   * Fonction de rappel lorsqu'un attribut est sélectionné.
   *
   * @param key La clé de l'attribut séléctionnée. Cette clé provient du `Dropdown.Item`.
   */
  const onAttributeSelect = useCallback(
    (key: string | null): void => {
      if (key !== null) {
        const metadataFilter = metadataFilters.find(m => m.id === key);
        if (metadataFilter !== undefined) {
          setFilters([
            ...filters,
            {
              kind: 'metadata',
              filter: metadataFilter,
              operation: metadataFilter.acceptedIndicatorFilterOperations[0],
              negate: false,
              value: '',
            },
          ]);
          setFilterShow(false);
          setCurrentFilter(metadataFilter.id);
          setFilterBeenModified(true);
          return;
        }

        const attribute = attributeData?.attributes?.items?.find(
          a => a.id === key
        );
        if (attribute !== undefined) {
          setFilters([
            ...filters,
            {
              kind: 'attribute',
              filter: mapAttributeToFilterType(attribute),
              operation: attribute.acceptedIndicatorFilterOperations[0],
              negate: false,
              value: '',
            },
          ]);
          setFilterShow(false);
          setCurrentFilter(attribute.id);
          setFilterBeenModified(true);
        }
      }
    },
    [attributeData, filters]
  );

  /**
   * Fonction de rappel lorsqu'un filtre est activé par un clic.
   *
   * @param filter Le filtre cliqué.
   */
  const onFilterToggle = (filter: { id: string }): void => {
    if (filter.id === currentFilter) {
      setCurrentFilter('');
    } else {
      setCurrentFilter(filter.id);
    }
  };
  
  /**
   * Fonction de rappel lorsqu'un(e) utilisateur(rice) sélectionne une opération de filtre.
   * @param key La clé de l'opération sélectionnée. Cette clé provient de `<Dropdown.Item>`.
   * @param index L'indice où est situé le filtre dans l'état du composant.
   * @param filter Le filtre modifié.
   */
  const onFilterOperationSelect = (
    key: string | null,
    index: number,
    filter: IndicatorFilter
  ): void => {
    if (key !== null) {
      filter.negate = key.includes('negate');
      const operation = key.split('-')[0];
      const operationValue = filter.filter.acceptedIndicatorFilterOperations.find(
        f => f.enumValue === operation
      );
      if (operationValue !== undefined) {
        filter.operation = operationValue;
        const newFilters = [
          ...filters.slice(0, index),
          filter,
          ...filters.slice(index + 1),
        ];
        setFilters(newFilters);
        setFilterBeenModified(true);
      }
    }
  };

    /**
   * Fonction de rappel pour un changement de valeur de filtre pour le Select.
   * @param filter Le filtre modifié
   * @param index L'indice où est situé le filtre dans l'état du composant.
   * @param event L'événement de changement de React.
   */
  const onSelectChange = (
    filter: IndicatorFilter,
    index: number,
    event: string
  ): void => {
    filter.value = event;
    const newFilters = [
      ...filters.slice(0, index),
      filter,
      ...filters.slice(index + 1),
    ];
    setFilters(newFilters);
    setFilterBeenModified(true);
  };

  /**
   * Fonction de rappel pour un changement de valeur de filtre.
   * @param filter Le filtre modifié
   * @param index L'indice où est situé le filtre dans l'état du composant.
   * @param event L'événement de changement de React.
   */
  const onFilterChange = (
    filter: IndicatorFilter,
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    console.log(event.target.value);
    filter.value = event.target.value;
    const newFilters = [
      ...filters.slice(0, index),
      filter,
      ...filters.slice(index + 1),
    ];
    setFilters(newFilters);
    setFilterBeenModified(true);
  };

  /**
   * Fonction de rappel lorsqu'une touche est pressée sur un filtre.
   *
   * Cette fonction existe puisque l'événement `change` des filtres
   * ne semble pas capturer la touche _espace_. Cet événement s'assure
   * que la touche espace est pris en compte lors de la saisie d'une
   * valeur pour le filtre.
   *
   * @param filter Le filtre concernée par l'événement
   * @param index L'indice où est situé le filtre dans l'état du composant.
   * @param event L'événement de saisie de React.
   */
  const onFilterKeyDown = (
    filter: IndicatorFilter,
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (event.key === ' ') {
      filter.value = `${filter.value}`;
      const newFilters = [
        ...filters.slice(0, index),
        filter,
        ...filters.slice(index + 1),
      ];
      setFilters(newFilters);
      setFilterBeenModified(true);
    }
  };

  /**
   * Foncition de rappel pour la suppression d'un filtre.
   *
   * @param index L'indice où est situé le filtre.
   */
  const onDeleteFilter = (index: number): void => {
    const newFilters = [
      ...filters.slice(0, index),
      ...filters.slice(index + 1),
    ];
    setFilters(newFilters);
    setFilterBeenModified(true);
  };
  /**
   * Fonction de rappel pour l'application des filtres.
   */
  const onFilterApply = useCallback((): void => {
    const attributeFilters = filters.filter(f => f.kind === 'attribute');
    const metadataFilter = filters.filter(f => f.kind === 'metadata');

    setSearch(
      attributeFilters.length === 0 ? undefined : {
        filters: attributeFilters.map(f => ({
          attributeId: f.filter.id,
          negate: f.negate,
          operation: f.operation.enumValue,
          value: f.value,
        })),
      }
    );
    setWhereStatement(buildMetadataWhereClause(metadataFilter));
    setFilterBeenModified(false);
  }, [filters]);

  /**
   * Fonction de rappel pour la fermeture de l'ajout d'un filtre.
   */
  const onFilterAddToggle = useCallback((): void => {
    setFilterShow(!filterShow);
  }, [filterShow]);

  /**
   * Fonction de rappel pour l'affichage des filtres.
   */
  const onFilterClick = useCallback((): void => {
    setFilterActive(!filterActive);
  }, [filterActive]);

  // <- Fonctions de rendu du contenu. ->

  const processDisposition = (
    mandatoryAttributes: string[]
  ): Array<(item: SimpleIndicator, index: number) => React.ReactElement> => {
    const renderFunctions = mandatoryAttributes.map<
      (item: SimpleIndicator, index: number) => React.ReactElement
    >(
      e =>
        function Mapper(item, i) {
          const temp = item.attributes.find(x => x.attribute.id === e);
          const value = temp?.primaryValue ?? temp?.referenceValue?.value ?? '';
          return <div key={i}>{value}</div>;
        }
    );

    renderFunctions.push(e => (
      <Stack direction="horizontal" gap={2}>
        <SimpleTooltip text="Consulter l'indicateur">
          <Button variant="primary" href={`/indicateurs/${e.id}`}>
            <FileEarmarkFill />
          </Button>
        </SimpleTooltip>
        {e.canBeEdited && (
          <SimpleTooltip text="Modifier l'indicateur">
            <Button href={`/indicateurs/${e.id}/modification`}>
              <PencilFill />
            </Button>
          </SimpleTooltip>
        )}
        {e.canBeDeleted && (
          <SimpleTooltip text="Supprimer l'indicateur">
            <ButtonLoading
              variant="danger"
              // skipcq JS-0417
              onClick={() => {
                onDeleteIndicator(e);
              }}
              loading={e.id === indicatorToDelete?.id && loading}
              testid={`delete-indicator-${e.id}`}
            >
              <Trash3Fill />
            </ButtonLoading>
          </SimpleTooltip>
        )}
      </Stack>
    ));

    return renderFunctions;
  };

  /**
   * Rendu du tableau qui contient les indicateurs.
   *
   * @param data Les indicateurs pour cette page avec les attributs obligatoires à afficher.
   *
   * @returns L'élément rendu.
   */
  const displayList = useCallback(
    (
      data: ListIndicatorsWithMandatoryAttributesQuery | undefined
    ): React.ReactElement => {
      const mandatoryAttributes = data?.attributes?.items?.map(a => a.id) ?? [];
      const titles = data?.attributes?.items?.map(a => a.name) ?? [];
      titles.push('Actions');
      const indicators: SimpleIndicator[] = data?.indicators?.items ?? [];
      const hasUndeletableIndicator = indicators.some(a => !a.canBeDeleted);

      return (
        <>
          {hasUndeletableIndicator && (
            <Alert variant="warning" dismissible>
              Au moins un indicateur détient actuellement des exemples. Le
              bouton de retrait de ces indicateurs est absent puisque ceux-ci ne
              peuvent être supprimés.
            </Alert>
          )}
          <SimpleActionTable<SimpleIndicator>
            titles={titles}
            disposition={processDisposition(mandatoryAttributes)}
            data={indicators}
            key={indicators.length}
            pageInfo={{
              skipped: skippedIndicators,
              took: pageSize,
              total: data?.indicators?.totalCount ?? 0,
            }}
            onPageSwitch={onPageSwitch}
            onPageSizeSwitch={onPageSizeChange}
          />
        </>
      );
    },
    [skippedIndicators, pageSize]
  );

  /**
   * Rendu des attributs pour les filtres.
   *
   * @returns L'élément rendu.
   */
  const renderAttributes = (): React.ReactElement => (
    <>
      {attributeData?.attributes?.items
        ?.filter(
          a => !filters.some(f => f.kind === 'attribute' && f.filter.id === a.id)
        )
        .map(a => (
          <Dropdown.Item key={a.id} eventKey={a.id}>
            {a.name}
          </Dropdown.Item>
        ))}
    </>
  );

  /**
   * Rendu des métadonnées pour les filtres.
   *
   * @returns L'élément rendu.
   */
  const renderMetadata = (): React.ReactElement => (
    <>
      {metadataFilters
        .filter(
          m => !filters.some(f => f.kind === 'metadata' && f.filter.id === m.id)
        )
        .map(m => (
          <Dropdown.Item key={m.id} eventKey={m.id}>
            {m.name}
          </Dropdown.Item>
        ))}
    </>
);

  /**
   * Rendu des opérations possibles pour chaque type.
   *
   * @returns L'élément rendu.
   */
  const renderFilterOperations = (
    filter: IndicatorFilter
  ): React.ReactElement => (
    <>
      {filter.filter.acceptedIndicatorFilterOperations.map(o => (
        <Fragment key={`${filter.filter.id}-${o.enumValue}`}>
          <Dropdown.Item eventKey={`${o.enumValue}`}>
            {o.readableValue}
          </Dropdown.Item>
          <Dropdown.Item eventKey={`${o.enumValue}-negate`}>
            {o.negatedReadableValue}
          </Dropdown.Item>
        </Fragment>
      ))}
    </>
  );

  /**
   * Rendu des filtres sélectionnées.
   *
   * @returns L'élément rendu.
   */
  const renderFilters = (): React.ReactElement => (
    <>
      {filters.map((f, i) => (
        <Col
          md={2}
          key={`filter-${f.kind}-${f.kind === 'attribute' ? f.filter.id : f.filter.id}`}
        >
          <Dropdown
            autoClose="outside"
            show={
              currentFilter === f.filter.id
            }
            // skipcq JS-0417
            onToggle={() => {
              onFilterToggle(f.filter);
            }}
          >
            <Dropdown.Toggle
              size="sm"
              variant={
                f.operation.enumValue !==
                  IndicatorFilterOperationOption.IsDefined && f.value === ''
                  ? 'light'
                  : 'info'
              }
            >
              {f.filter.name}
            </Dropdown.Toggle>
            <Dropdown.Menu className="filter-dropdown">
              <Dropdown.Item className="no-highlight" as="div">
                <SimpleTooltip text="supprimer le filtre">
                  <Button
                    size="sm"
                    variant="danger"
                    className="ms-auto"
                    data-testid={`delete-${f.filter.id}`}
                    // skipcq JS-0417
                    onClick={() => {
                      onDeleteFilter(i);
                    }}
                  >
                    <Trash3Fill />
                  </Button>
                </SimpleTooltip>
              </Dropdown.Item>
              <Dropdown.Item className="no-highlight" as="div">
                <Dropdown
                  // skipcq JS-0417
                  onSelect={key => {
                    onFilterOperationSelect(key, i, f);
                  }}
                >
                  <Dropdown.Toggle variant="light">
                    {f.negate
                      ? f.operation.negatedReadableValue
                      : f.operation.readableValue}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {renderFilterOperations(f)}
                  </Dropdown.Menu>
                </Dropdown>
              </Dropdown.Item>
              {f.operation.enumValue !== 'IS_DEFINED' && (
                f.kind === 'attribute' &&
                f.filter.valueType === FilterValueOption.List ? (
                  <Dropdown.Item className="no-highlight" as="div">
                    <FloatingSelectInput
                      id="attribute-dropdown"
                      className="mb-3"
                      label="Contenu du filtre..."
                      defaultElement={f.value ?? ''}
                      elements={f.filter.values?.map(v => v.value) ?? []}
                      values={f.filter.values?.map(v => v.value) ?? []}
                      onElementChange={event => {
                        onSelectChange(f, i, event);
                      }}
                      required
                      invalidFeedback="Le type d'attribut est obligatoire"
                    />
                  </Dropdown.Item>
                ) : f.kind === 'metadata' && f.filter.valueType === FilterValueOption.Date ? (
                  <Dropdown.Item className="no-highlight" as="div">
                    <FloatingDateInput
                      id={`date-${f.filter.id}`}
                      testId={`test-date-${f.filter.id}`}
                      label="Contenu du filtre..."
                      value={f.value}
                      onChange={event => {
                        onFilterChange(f, i, event);
                      }}
                      required={false}
                    />
                  </Dropdown.Item>
                ) : (
                  <Dropdown.Item className="no-highlight" as="div">
                    <FloatingTextInput
                      id={`${f.kind}-${f.kind === 'attribute' ? f.filter.id : f.filter.id}`}
                      testId={`test-${f.kind}-${f.kind === 'attribute' ? f.filter.id : f.filter.id}`}
                      label="Contenu du filtre..."
                      value={f.value}
                      onChange={event => {
                        onFilterChange(f, i, event);
                      }}
                      onKeyDown={event => {
                        onFilterKeyDown(f, i, event);
                      }}
                      required={false}
                    />
                  </Dropdown.Item>
                )
              )}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      ))}
    </>
  );

  return (
    <>
      <Row className="justify-content-md-center mb-2">
        <Col md={10}>
          <Row className="justify-content-md-start">
            <Col md={9}>
              <div className="h2">Liste des indicateurs</div>
            </Col>
            <Col md={3}>
              <div className="d-grid">
                <Button variant="success" href="/indicateurs/ajout">
                  Ajouter
                </Button>
              </div>
            </Col>
          </Row>
          {attributeError !== undefined && (
            <Alert variant="danger">{attributeError.message}</Alert>
          )}
          <Row className="mb-2">
            <Col md={2}>
              <Button size="sm" active={filterActive} onClick={onFilterClick}>
                Filtrer
              </Button>
            </Col>
          </Row>
          {filterActive && (
            <Row className="mb-2">
              {renderFilters()}
              <Col md={2}>
                <Dropdown
                  autoClose="outside"
                  onSelect={onAttributeSelect}
                  show={filterShow}
                  onToggle={onFilterAddToggle}
                >
                  <Dropdown.Toggle size="sm" variant="secondary">
                    Ajouter un filtre
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item as="div" className="no-highlight">
                      <FloatingTextInput
                        id="attribute-to-filter"
                        testId="attribute-to-filter"
                        label="Filtrer par..."
                        value={attributeName}
                        onChange={onAttributeChange}
                        required={false}
                      />
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Header>Attributs</Dropdown.Header>
                    {attributesLoading && (
                      <Dropdown.Item as="div" className="no-highlight">
                        <span className="spinner-border spinner-border-sm me-2" />
                      </Dropdown.Item>
                    )}
                    {!attributesLoading && renderAttributes()}
                    <Dropdown.Header>Métadonnées</Dropdown.Header>
                    {renderMetadata()}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              {hasFilterBeenModified && (
                <Col md={2}>
                  <Button size="sm" onClick={onFilterApply}>
                    Appliquer
                  </Button>
                </Col>
              )}
            </Row>
          )}
          <Row>
            <ContentLoader
              data={displayList}
              query={LIST_INDICATORS_WITH_MANDATORY_ATTRIBUTE}
              variables={{ skip: skippedIndicators, take: pageSize, search, where: whereStatement }}
            />
          </Row>
        </Col>
      </Row>
      <SimpleActionModal
        successMessage={successDeleteMessage}
        errorMessages={deleteErrors}
        genericErrorMessage="Des erreurs sont survenues lors de la suppression de l'indicateur"
        show={confirmModalShow}
        onClose={onClose}
        onConfirm={onConfirm}
        title="Confirmer la suppression"
        confirmButtonText="Supprimer l'indicateur"
      >
        <>
          <p>Êtes-vous certain(e) de vouloir supprimer l&apos;indicateur?</p>
          <p>L&apos;action est irréversible.</p>
        </>
      </SimpleActionModal>
    </>
  );
}
